import {
  OutputContentId,
  typedObjectEntries,
  typedObjectFromEntries,
} from "@hex/common";
import type {
  ActionCreatorWithPayload,
  ActionCreatorWithoutPayload,
} from "@reduxjs/toolkit";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import type {
  ResolvedExternalContext,
  ThreadInlineReferenceSummary,
  ThreadInlineReferenceUpdates,
} from "../../components/agent/ask-from-home/ThreadInlineContextInfo.helpers";
import { RootState } from "../store";

export type ThreadInlineReferenceEntry = {
  summary: ThreadInlineReferenceSummary | undefined;
  context: ResolvedExternalContext | undefined;
};

export interface ThreadInlineState {
  /**
   * This is for downstream components of ThreadInlineContextInfo
   *
   * referenceId → entry (summary + context for the UI)
   **/
  references: Record<string, ThreadInlineReferenceEntry | undefined>;

  // Internal stuff for thread inline context

  /** referenceIds we've already kicked off a fetch for (dedup guard) */
  fetchedKeys: Record<string, true | undefined>;
  /** Output content IDs already seeded, to avoid duplicate dispatches. */
  syncedOutputContentIds: Record<string, true | undefined>;
}

const initialState: ThreadInlineState = {
  references: {},
  fetchedKeys: {},
  syncedOutputContentIds: {},
};

const threadInlineSlice = createSlice({
  name: "threadInline",
  initialState,
  reducers: {
    /** Mark referenceIds as in-flight (prevents duplicate fetches). */
    markFetched(state, action: PayloadAction<string[]>) {
      for (const key of action.payload) {
        state.fetchedKeys[key] = true;
      }
    },
    /** Remove referenceIds from the fetched set (allows retry on error). */
    unmarkFetched(state, action: PayloadAction<string[]>) {
      for (const key of action.payload) {
        delete state.fetchedKeys[key];
      }
    },
    /** Merge reference updates (summaries + contexts) into the lookup table. */
    mergeReferenceUpdates(
      state,
      action: PayloadAction<ThreadInlineReferenceUpdates>,
    ) {
      const { contextUpdates, summaryUpdates } = action.payload;
      return {
        ...state,
        references: {
          ...state.references,
          ...typedObjectFromEntries(
            typedObjectEntries(summaryUpdates).map(([refId, summary]) => [
              refId,
              { summary, context: contextUpdates[refId] },
            ]),
          ),
        },
      };
    },
    /** Track which output content IDs have been seeded. */
    trackSyncedOutputContent(state, action: PayloadAction<OutputContentId[]>) {
      for (const id of action.payload) {
        state.syncedOutputContentIds[id] = true;
      }
    },
    /** Reset all state (e.g. when the provider unmounts or the thread changes). */
    reset() {
      return initialState;
    },
  },
});

export const threadInlineSelectors = {
  selectReferences: (state: RootState) => state.threadInline.references,
  selectReference: (state: RootState, referenceId: string) =>
    state.threadInline.references[referenceId],
  selectFetchedKeys: (state: RootState) => state.threadInline.fetchedKeys,
  selectIsFetched: (state: RootState, referenceId: string) =>
    state.threadInline.fetchedKeys[referenceId] === true,
  selectSyncedOutputContentIds: (state: RootState) =>
    state.threadInline.syncedOutputContentIds,
};

export const threadInlineReducer = threadInlineSlice.reducer;

interface ThreadInlineActions {
  markFetched: ActionCreatorWithPayload<string[]>;
  unmarkFetched: ActionCreatorWithPayload<string[]>;
  mergeReferenceUpdates: ActionCreatorWithPayload<ThreadInlineReferenceUpdates>;
  trackSyncedOutputContent: ActionCreatorWithPayload<OutputContentId[]>;
  reset: ActionCreatorWithoutPayload;
}
export const threadInlineActions: ThreadInlineActions =
  threadInlineSlice.actions;
